import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection } from 'constants/index'
import { signOut } from 'store/auth'
import moment from 'moment'
import { convertTimestampToLocalString, sortDataArrayByCreatedAt } from 'utils/times'

export const ALL_DATES_FILTER = 'All Dates'

export const initialState = {
  error: null,
  inProgress: false,
  sessions: [],
  subEvents: [],
  session: null,
  selectedDateFilter: ALL_DATES_FILTER
}

const sessionSlice = createSlice({
  name: 'session',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setSessions: (state, action) => {
      state.sessions = action.payload
    },
    setSubEvents: (state, action) => {
      state.subEvents = action.payload
    },
    setSession: (state, action) => {
      state.session = action.payload
    },
    setSelectedDateFilter: (state, action) => {
      state.selectedDateFilter = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.sessions = []
      state.session = null
      state.associatedPolls = []
      state.subEvents = []
      state.selectedDateFilter = ALL_DATES_FILTER
    }
  }
})

export const addSession = async (conferenceId, payload) => {
  try {
    const doc = await FirestoreDatabase.collection(Collection.SESSIONS).add({
      ...payload,
      conferenceId,
      createdAt: moment().utc().valueOf()
    })
    return doc.id
  } catch (err) {
    console.error(err)
  }
}

export const fetchSessions = (eventId, conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.SESSIONS)
    .where('eventId', '==', eventId)
    .where('conferenceId', '==', conferenceId)
    .orderBy('startDate')
    .onSnapshot((documents) => {
      const sessions = []
      documents.forEach((doc) => {
        sessions.push({
          id: doc.id,
          ...doc.data(),
          type: 'Sub Event Poll'
        })
      })
      callback(sortDataArrayByCreatedAt(sessions).map((item, i) => ({ ...item, srNo: i + 1 })))
    })
}

export const getAllSubEvents = (conference) => async (dispatch) => {
  try {
    const { id: conferenceId, timezone, is24hrFormat } = conference
    const snapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('conferenceId', '==', conferenceId)
      .orderBy('startDate')
      .get()
    const subEvents = snapshot.docs.map((doc) => {
      return { id: doc?.id, ...doc.data() }
    })

    const eventsSnapshot = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('conferenceId', '==', conferenceId)
      .get()
    const events = eventsSnapshot.docs.map((doc) => {
      return { id: doc?.id, ...doc.data() }
    })

    const parsedSubEvents = subEvents.map((subEvent) => {
      const event = events.find((event) => event.id === subEvent.eventId)
      return {
        ...subEvent,
        startDate: convertTimestampToLocalString(subEvent?.startDate, timezone, is24hrFormat),
        endDate: convertTimestampToLocalString(subEvent?.endDate, timezone, is24hrFormat),
        event: {
          id: event?.id,
          title: event?.title,
          startDate: convertTimestampToLocalString(event?.startDate, timezone, is24hrFormat),
          endDate: convertTimestampToLocalString(event?.endDate, timezone, is24hrFormat)
        }
      }
    })

    dispatch(
      sessionSlice.actions.setSubEvents(
        sortDataArrayByCreatedAt(parsedSubEvents).map((item, i) => ({ ...item, srNo: i + 1 }))
      )
    )
  } catch (err) {
    console.log(err)
  }
}

export const getConfSubEvents = async (conferenceId) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('conferenceId', '==', conferenceId)
      .orderBy('startDate')
      .get()
    const subEvents = snapshot.docs.map((doc) => ({ id: doc?.id, ...doc.data() }))
    return subEvents
  } catch (err) {
    console.log(err)
  }
}

export const fetchSessionById = (sessionId) => async (dispatch) => {
  try {
    dispatch(sessionSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SESSIONS).doc(sessionId).get()
    const e = snapshot.data()
    dispatch(
      sessionSlice.actions.setSession({
        id: snapshot.id,
        ...e
      })
    )
    dispatch(sessionSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(sessionSlice.actions.setError(err))
    dispatch(sessionSlice.actions.setProgress(false))
  }
}

export const updateSessionById = async (sessionId, payload) => {
  try {
    await FirestoreDatabase.collection(Collection.SESSIONS).doc(sessionId).update(payload)
    return true
  } catch (err) {
    console.error(err)
  }
}

export const deleteSessionById = (sessionId) => async (dispatch) => {
  try {
    const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
      .where('sessionId', '==', sessionId)
      .get()

    const talks = talksSnap.docs.map((doc) => {
      return { id: doc?.id, ...doc.data() }
    })

    const pollIdsList = talks
      .filter((talk) => talk.havePolls === true)
      .flatMap((talk) => talk?.pollIds ?? (talk?.pollId ? [talk.pollId] : []))

    const batch = FirestoreDatabase.batch()
    const pollsColRef = FirestoreDatabase.collection(Collection.POLLS)

    const sessionDoc = await FirestoreDatabase.collection(Collection.SESSIONS).doc(sessionId).get()
    const session = { id: sessionDoc?.id, ...sessionDoc.data() }
    if (session?.havePolls) {
      pollIdsList.push(...(session?.pollIds ?? (session?.pollId ? [session.pollId] : [])))
    }

    // Delete session attachments
    const sessionAttachmentsSnapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
      .doc(sessionId)
      .collection(Collection.ATTACHMENTS)
      .get()

    // Delete each session attachment document
    sessionAttachmentsSnapshot.docs.forEach((doc) => {
      const attachmentRef = FirestoreDatabase.collection(Collection.SESSIONS)
        .doc(sessionId)
        .collection(Collection.ATTACHMENTS)
        .doc(doc.id)
      batch.delete(attachmentRef)
    })

    pollIdsList.forEach((pollId) => {
      const docRef = pollsColRef.doc(pollId)
      batch.update(docRef, {
        alreadyUsed: false,
        eventId: null,
        sessionId: null,
        talkId: null
      })
    })

    // For each talk associated with this session, also delete their attachments
    for (const talk of talks) {
      // Delete talk attachments
      const talkAttachmentsSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
        .doc(talk.id)
        .collection(Collection.ATTACHMENTS)
        .get()

      // Delete each talk attachment document
      talkAttachmentsSnapshot.docs.forEach((doc) => {
        const attachmentRef = FirestoreDatabase.collection(Collection.TALKS)
          .doc(talk.id)
          .collection(Collection.ATTACHMENTS)
          .doc(doc.id)
        batch.delete(attachmentRef)
      })

      // Delete talk document
      const talkDocRef = FirestoreDatabase.collection(Collection.TALKS).doc(talk?.id)
      batch.delete(talkDocRef)
    }

    const sessionDocRef = FirestoreDatabase.collection(Collection.SESSIONS).doc(sessionId)
    batch.delete(sessionDocRef)
    await batch.commit()
  } catch (err) {
    dispatch(sessionSlice.actions.setError(err))
  }
}

export const resetSessionStore = () => (dispatch) => {
  try {
    dispatch(sessionSlice.actions.setSession(null))
  } catch (err) {
    dispatch(sessionSlice.actions.setError(err))
  }
}

export default sessionSlice
